/* Generated by Claude Code */

import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import styles from './HoverTooltip.module.scss'
import {
  HoverTooltipPlacement,
  HoverTooltipProps,
  HoverTooltipSide,
} from './HoverTooltip.types'

type Size = { width: number; height: number }
type Point = { top: number; left: number }

const SIDE_FALLBACK_ORDER: HoverTooltipSide[] = [
  'top',
  'bottom',
  'right',
  'left',
]

const OPPOSITE_SIDE: Record<HoverTooltipSide, HoverTooltipSide> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
}

function getSide(placement: HoverTooltipPlacement): HoverTooltipSide {
  return placement.split('-')[0] as HoverTooltipSide
}

function getAlignment(placement: HoverTooltipPlacement): string | undefined {
  return placement.split('-')[1]
}

function computePoint(
  target: DOMRect,
  size: Size,
  placement: HoverTooltipPlacement,
  offset: number
): Point {
  const side = getSide(placement)
  const alignment = getAlignment(placement)

  if (side === 'top' || side === 'bottom') {
    const top =
      side === 'top'
        ? target.top - size.height - offset
        : target.bottom + offset
    let left: number
    if (alignment === 'left') {
      left = target.left
    } else if (alignment === 'right') {
      left = target.right - size.width
    } else {
      left = target.left + target.width / 2 - size.width / 2
    }
    return { top, left }
  }

  const left =
    side === 'left' ? target.left - size.width - offset : target.right + offset
  let top: number
  if (alignment === 'top') {
    top = target.top
  } else if (alignment === 'bottom') {
    top = target.bottom - size.height
  } else {
    top = target.top + target.height / 2 - size.height / 2
  }
  return { top, left }
}

function fitsInViewport(point: Point, size: Size): boolean {
  return (
    point.top >= 0 &&
    point.left >= 0 &&
    point.top + size.height <= window.innerHeight &&
    point.left + size.width <= window.innerWidth
  )
}

function resolvePlacement(
  target: DOMRect,
  size: Size,
  preferred: HoverTooltipPlacement | 'auto',
  offset: number
): HoverTooltipPlacement {
  let order: HoverTooltipPlacement[]

  if (preferred === 'auto') {
    order = SIDE_FALLBACK_ORDER
  } else {
    const preferredSide = getSide(preferred)
    const alignment = getAlignment(preferred)
    const flipped = OPPOSITE_SIDE[preferredSide]
    const sameAxisFlip = (
      alignment ? `${flipped}-${alignment}` : flipped
    ) as HoverTooltipPlacement
    const remainingSides = SIDE_FALLBACK_ORDER.filter(
      (s) => s !== preferredSide && s !== flipped
    )

    order = [preferred, sameAxisFlip, ...remainingSides]
  }

  for (const placement of order) {
    if (fitsInViewport(computePoint(target, size, placement, offset), size)) {
      return placement
    }
  }
  return order[0]
}

function clampToViewport(point: Point, size: Size): Point {
  return {
    top: Math.max(0, Math.min(point.top, window.innerHeight - size.height)),
    left: Math.max(0, Math.min(point.left, window.innerWidth - size.width)),
  }
}

export function HoverTooltip({
  content,
  children,
  placement = 'auto',
  offset = 8,
  className,
}: HoverTooltipProps) {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState<Point | null>(null)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!visible || !triggerRef.current || !tooltipRef.current) {
      return
    }
    const targetRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const size: Size = {
      width: tooltipRect.width,
      height: tooltipRect.height,
    }
    const chosen = resolvePlacement(targetRect, size, placement, offset)
    const point = computePoint(targetRect, size, chosen, offset)
    setPosition(clampToViewport(point, size))
  }, [visible, placement, offset, content])

  const show = () => setVisible(true)
  const hide = () => {
    setVisible(false)
    setPosition(null)
  }

  return (
    <>
      <span
        ref={triggerRef}
        className={styles.trigger}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}>
        {children}
      </span>
      {visible &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            role='tooltip'
            className={classNames(styles.tooltip, className)}
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}>
            {content}
          </div>,
          document.body
        )}
    </>
  )
}
